import type { PortalEvent } from '@src/lit/portal.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { nanoid } from 'nanoid';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { customerAttributesDiffer } from '@common/customerAttributes/customerAttributesDiffer.js';
import { customerAttributeModeMatcher } from '@common/customerAttributes/TefComsCustomerAttributeInlineBase.js';
import { CustomerAttribute } from '@common/customerAttributes/types.js';
import { CompVisibleMixin } from '@common/lit/mixins/CompVisibleMixin.js';
import { type TefComs as TefComsCommon } from '@common/types/tefComs.js';
import { LitElementExtended } from '@src/classes/LitElementExtended.js';
import { config } from '@src/config/index.js';
import { service } from '@src/service/index.js';
import { tracker as tefGtmTracker } from '@src/service/tracker.js';
import { validate } from '@src/store/auth.js';
import {
  init, occlThemeLoad, occlThemeError, logMicrofrontendParams,
} from '@src/store/config.js';
import {
  load, save, setGrantedValueForGroups, addRenderedConsentVersionId, removeRenderedConsentVersionId,
} from '@src/store/consents.js';
import { store, RESET, RootState } from '@src/store/index.js';
import { events } from '@src/store/middleware/events.js';
import { SESSION_STATE_KEY } from '@src/store/middleware/persist.js';
import type { ConsentGroup, PageDetails } from '@src/types/common.js';
import { type TefComs as TefComsType } from '@src/types/typings.js';

import '@src/components/tef-coms-consent-group.js';
import '@src/components/tef-coms-customer-attribute.js';
import '@src/components/tef-coms-customer-attribute-inline.js';

const CompositeClass = CompVisibleMixin(connect(store)(LitElementExtended));

type StringUndef = string | undefined;
type ConsentGroupsUndef = ConsentGroup[] | undefined;
type CustomerAttributesUndef = CustomerAttribute[] | undefined;

@customElement('tef-coms')
export class TefComs extends CompositeClass {
  // externally setable attributes:
  @property({ type: String }) accessToken: StringUndef = undefined;
  @property({ type: String }) accesstoken4p: StringUndef = undefined;
  @property({ type: String }) apiUri: string = 'http://localhost:3000';
  @property({ type: String }) apiUri4p: StringUndef = undefined;
  @property({ type: String }) channel: string = 'Web';
  @property({ type: String }) comment: StringUndef = undefined;
  @property({ type: String }) customerToken: StringUndef = undefined;
  @property({ type: String }) group: StringUndef = undefined;
  @property({ type: String }) lcId: StringUndef = undefined;
  @property({ type: String }) lifecycle: StringUndef = undefined;
  @property({ type: String }) mode: string = '';
  @property({ type: Boolean }) newcustomer: boolean = false;
  @property({ type: String }) occluri: StringUndef = undefined;
  @property({ type: String }) references: StringUndef = undefined;
  @property({ type: String }) registerId: StringUndef = undefined;
  @property({ type: String }) registerToken: StringUndef = undefined;
  @property({ type: String }) spId: StringUndef = undefined;
  @property({ type: String }) sessionkey: StringUndef = undefined;
  @property({ type: String }) stylecontext: string = '';
  @property({ type: String }) traceid: string = nanoid();
  @property({ type: String }) usertype: StringUndef = undefined;
  @property({ type: String }) vonr: string = '';

  // Internal reactive state
  @state() consentGroups: ConsentGroup[] = [];
  @state() consentGroupsOrigin: ConsentGroup[] = [];
  @state() consentGroupsView: ConsentGroupsUndef = undefined;
  @state() customerAttributes: CustomerAttributesUndef = undefined;
  @state() customerAttributesOrigin: CustomerAttributesUndef = undefined;
  @state() details: PageDetails | undefined = undefined;
  @state() editmodeActiveGroupInd: number = -1;
  @state() fatal: boolean = false;
  @state() globalOfferId: StringUndef = undefined;
  @state() initializing: boolean = true;
  @state() mergeAccepted: boolean = false;

  eventTracking: Record<string, number> = {};

  constructor() {
    super();

    service.bindTefComs(this);
    store.dispatch({ type: RESET });
    service.tracker = tefGtmTracker;
  }

  async connectedCallback() {
    try {
      if (localStorage.getItem('tef-coms-debug-mode')) {
        // @ts-expect-error: TODO -- remove this when we have a better solution for this
        let timestampInit: DOMHighResTimeStamp = null;
        let timestampReady = null;

        this.addEventListener('tef-coms', (e) => {
          const { detail } = e as TefComsCommon.Event;
          const channel = detail.level === 'error' ? 'error' : 'info';
          console[channel]('%ctef-coms: event%c', 'color: rebeccapurple', 'color: #000', detail.name, e); // eslint-disable-line no-console
          if (detail.name === 'config/INIT') {
            timestampInit = performance && performance.now ? performance.now() : 0;
          }
          if (detail.name === 'load/READY') {
            timestampReady = performance && performance.now ? performance.now() : 0;
            const el = customElements.get('tef-coms') as unknown as TefComsType.MicroFrontend;
            const infoVersion = `version ${el.version}, build ${el.build.target}`;
            const infoReady = `Micro-frontend ready in ${Math.trunc(timestampReady - timestampInit)} ms, `;
            console.log(infoReady + infoVersion); // eslint-disable-line no-console
          }
        });
      }

      store.dispatch({ type: 'CONNECTED' }); // used for performance measurements !!

      super.connectedCallback(); // start rendering immediately !!

      this.attachCustomerAttributeModalRenderedHandler();

      // Only in group mode we need this
      if (this.group) {
        this.installIntersectionObserver();
      }
      await this.load();
    } catch (_error) { // eslint-disable-line sonarjs/no-ignored-exceptions
      // ignore -- has error condition instead
    }
  }

  componentBecameVisible() {
    this.updateRenderedConsentVersionIds();
  }

  attachCustomerAttributeModalRenderedHandler() {
    // If the customer attribute "portal" is rendered we need to substitute stuff
    // like we do in "updated" here.
    // This event is triggered by MFEC -> portal.ts
    this.renderRoot.addEventListener('portal-rendered', (event: unknown) => {
      const modalElement = (event as PortalEvent).detail.contentElement;
      if (modalElement) {
        this.replaceElements(modalElement);
      }
    });
  }

  async load() {
    const {
      accessToken,
      accesstoken4p,
      apiUri,
      apiUri4p,
      channel,
      comment,
      customerToken,
      group,
      lcId,
      lifecycle,
      mode,
      newcustomer,
      occluri,
      references,
      registerId,
      registerToken,
      sessionkey,
      spId,
      stylecontext,
      traceid,
      usertype,
      vonr,
    } = this;

    // Get the sessionkey before we overwrite it with the current one
    // which results in this.sessionkey always === store.sessionkey
    const { config: { sessionkey: sessionKeyFromSessionStorage } } = store.getState();

    await store.dispatch(
      init({
        accessToken,
        accesstoken4p,
        apiUri,
        apiUri4p,
        channel,
        comment,
        customerToken,
        group,
        lcId,
        lifecycle,
        mode,
        newcustomer,
        occluri,
        references,
        registerId,
        registerToken,
        sessionkey,
        spId,
        stylecontext,
        traceid,
        usertype,
        vonr,
        generation: 1,
      }),
    );
    await store.dispatch(validate());
    store.dispatch(logMicrofrontendParams()); // This must come after validate() since that sets "auth" ...
    await store.dispatch(load(sessionKeyFromSessionStorage));

    store.dispatch(setGrantedValueForGroups());
  }

  async save() {
    await store.dispatch(save()); // eslint-disable-line -- "Been here for ages"
  }

  stateChanged(rootState: RootState) {
    // state changes from Redux (connect-mixin)
    const {
      config: { occlReady, error: configError },
      auth: { processing: authProcessing, error: authError },
      consents: {
        processing: consentsProcessing,
        error: consentsError,
        consentGroups,
        consentGroupsView,
        consentGroupsOrigin,
        editmodeActiveGroupInd,
        details,
        mergeAccepted,
        customerAttributes,
        customerAttributesOrigin,
      },
      globalOfferId,
    } = rootState;

    // map state to props
    this.consentGroups = consentGroups!;
    this.details = details!;
    this.mergeAccepted = mergeAccepted;
    this.consentGroupsView = consentGroupsView!;
    this.consentGroupsOrigin = consentGroupsOrigin!;
    this.editmodeActiveGroupInd = editmodeActiveGroupInd;
    this.globalOfferId = globalOfferId;
    this.initializing = !occlReady || authProcessing || (consentsProcessing && !consentGroups);
    this.fatal = (configError || authError || (consentGroups ? undefined : consentsError))!;
    this.customerAttributes = customerAttributes!;
    this.customerAttributesOrigin = customerAttributesOrigin!;
  }

  updated() {
    this.replaceElements();
  }

  /**
   * Removes a given consent group from the list of "seen" consent groups.
   * This can be used by an integrator together with the "group" property.
   *
   * For example:
   * If there are two page one rendering group=iban and one rendering no group you could use
   * TefComs.removeConsentGroup("iban") to remove the iban group from the list of "seen" consents.
   *
   * This will cause the "iban" consents to NOT BE SAVED to the database as if the customer has never "seen" the group at all.
   *
   * Usage:
   * window.customElements.get("tef-coms").removeConsentGroup("einwilligungen");
   */
  static removeConsentGroup(consentGroup: string): string[] {
    const sessionStorage = window.sessionStorage.getItem(SESSION_STATE_KEY);
    if (!sessionStorage) return [];

    const sessionState = JSON.parse(sessionStorage) as RootState;
    const renderedConsentVersionIds = sessionState.consents?.renderedConsentVersionIds;

    let cleanedRenderedConsentVersionIds = renderedConsentVersionIds;
    if (renderedConsentVersionIds) {
      const group = consentGroup.trim();
      cleanedRenderedConsentVersionIds = renderedConsentVersionIds.filter((renderedConsentVersionId) => !renderedConsentVersionId.startsWith(`${group}_`));
      sessionState.consents.renderedConsentVersionIds = cleanedRenderedConsentVersionIds;
      window.sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(sessionState));
    }

    return cleanedRenderedConsentVersionIds;
  }

  /**
   * returns filtered list of consentGroup for rendering.
   *
   * Either returns the complete list or - in case of property "group" usage - the requested consent groups
   */
  get displayConsentGroups() {
    // No "groups" attribute
    if (!this.group || this.group === '') {
      return this.consentGroups;
    }

    // We might get called to early
    if (!this.consentGroups) {
      return [];
    }

    const finalGroups: ConsentGroup[] = [];
    const groups: string[] = this.group.split(',').map((g) => g.toLowerCase().trim());

    // Put groups into final in the wanted order
    groups.forEach((groupName) => {
      const consentGroup = this.consentGroups.find((foundGroup) => foundGroup.name.toLowerCase().trim() === groupName);

      if (consentGroup) {
        // Add consent to list and also add the generation
        finalGroups.push(consentGroup);
      }
    });

    // No groups found -> empty
    if (finalGroups.length === 0 && !this.eventTracking['consents/EMPTY']) {
      const eventData = events.find((event) => event.name === 'consents/EMPTY')!;
      service.fireEvent('consents/EMPTY', { level: eventData.level ?? 'info', message: `${eventData.message} (groups)` });
      this.eventTracking['consents/EMPTY'] = 1;
      return [];
    }

    return finalGroups;
  }

  /**
   * Used if mode="multiedit"
   */
  get viewTemplate() {
    const {
      consents: { editmodeActiveGroupInd },
    } = store.getState();
    if (editmodeActiveGroupInd === -1) {
      return this.template;
    }

    const consentGroup = this.consentGroups[editmodeActiveGroupInd];
    const viewTemplate = this.getTemplateFn('viewTemplate');

    return [viewTemplate(consentGroup, editmodeActiveGroupInd, this.hasChangedConsentsOrCustomerAttributes)];
  }

  updateRenderedConsentVersionIds() {
    this.displayConsentGroups.forEach((consentGroup) => {
      if (this.isDisplayModeHidden(consentGroup.displayMode) && typeof consentGroup.grantedDefault === 'undefined') {
        this.removeRenderedConsentGroup(consentGroup);
      } else {
        this.addRenderedConsentGroup(consentGroup);
      }
    });
  }

  /**
   * Used if mode !== "multiedit"
   */
  get template() {
    let buttonMode;
    let buttonModeButton;

    if (this.displayConsentGroups.length > 0 && this.mode?.includes('buttons')) {
      [buttonMode, buttonModeButton] = this.mode.split(':');
    }

    const consentGroupTemplate = this.getTemplateFn('consentGroupTemplate');
    const htmls = [];

    this.displayConsentGroups.forEach((consentGroup) => {
      // We need to search the index in the original consentGroups for usage in multiedit
      // displayConsentGroups will be filtered by displayMode=hidden (see below) and this will screw up the index
      // in the templates
      const indexInConsentGroups = this.consentGroups.findIndex(
        (group) => group.name.toLowerCase().trim() === consentGroup.name.toLowerCase().trim(),
      );
      if (!this.isDisplayModeHidden(consentGroup.displayMode)) {
        htmls.push(consentGroupTemplate(consentGroup, indexInConsentGroups, this));
      }
    });
    this.updateRenderedConsentVersionIds();

    htmls.push(html`${this.pageFooter}`);
    if (buttonMode) {
      const buttonModeTemplate = this.getTemplateFn('buttonModeTemplate');
      htmls.push(buttonModeTemplate(buttonModeButton, this.hasChangedConsentsOrCustomerAttributes));
    }

    if (this.details?.merge) {
      const mergeAccountsWrapperTemplate = this.getTemplateFn('mergeAccountsWrapper');
      return [mergeAccountsWrapperTemplate(this.details.merge, htmls, this.mergeAccepted)];
    }

    return htmls;
  }

  /**
   * Mark all consent version ids inside a consent group as rendered/processed.
   */
  addRenderedConsentGroup(consentGroup: ConsentGroup) {
    consentGroup.consents.forEach((consent) => {
      store.dispatch(addRenderedConsentVersionId(consentGroup.name, consent.id));
    });
  }

  /**
   * Remove all consent version ids inside a consent group from the list of rendered/processed consents.
   */
  removeRenderedConsentGroup(consentGroup: ConsentGroup) {
    consentGroup.consents.forEach((consent) => {
      store.dispatch(removeRenderedConsentVersionId(consent.id));
    });
  }

  get pageFooter() {
    const pageFooter = this.getTemplateFn('pageFooter');
    return this.displayConsentGroups.length > 0 && this.details && this.details.pageFooter ? pageFooter(this) : nothing;
  }

  render() {
    const { config: { occluri = '' } } = store.getState();
    // Render nothing on error
    if (this.fatal) {
      return [html`<!-- fatal error condition -->`];
    }

    // Render as OCCL?
    if (occluri) {
      return this.renderWithOccl();
    }

    // Render as TefOne
    return [html`<one-stack>${this.renderTemplate()}</one-stack>`];
  }

  renderWithOccl() {
    const {
      occl: { theme },
    } = config;

    const {
      config: { stylecontext = '', occluri = '' },
    } = store.getState();

    const themeUri = theme(occluri);
    const classes = [
      ...stylecontext.split(' ').filter((s) => s).map((s) => `context-${s}`),
      'tef-coms',
      'section',
      'section--size-flat',
    ];

    const occlLoad = () => store.dispatch(occlThemeLoad());
    const occlError = () => store.dispatch(occlThemeError());

    return [
      html`<link rel="stylesheet" href="${themeUri}" @load="${occlLoad}" @error="${occlError}" />`,
      this.initializing ? nothing : html`<div class=${classes.join(' ')}>${this.renderTemplate()}</div>`,
    ];
  }

  renderTemplate() {
    if (!this.displayConsentGroups) {
      return html`${nothing}`;
    }

    if (this.isSingleCustomerAttributeEditMode) {
      return html`<tef-coms-customer-attribute-inline mode="${this.mode}"></tef-coms-customer-attribute-inline>`;
    }

    if (this.isMultieditMode) {
      return html`${this.viewTemplate}`;
    }
    return html`${this.template}`;
  }

  get hasChangedConsentsOrCustomerAttributes() {
    const hasChangedConsents = this.consentGroups.reduce(
      (acc, currentValue, currentIndex) => acc
        || JSON.stringify(currentValue.consents) !== JSON.stringify(this.consentGroupsOrigin[currentIndex].consents)
        || !!currentValue.consents.find((c) => c.modeconstraint === 'edit'),
      false,
    );

    return hasChangedConsents || customerAttributesDiffer(this.customerAttributes, this.customerAttributesOrigin);
  }

  get isMultieditMode() {
    return !!(this.mode && this.mode.includes('multiedit'));
  }

  get isSingleCustomerAttributeEditMode() {
    return customerAttributeModeMatcher.test(this.mode);
  }
}
